import logoLight from '../../../assets/branding/logo_horizontal_TextoAzulEscuro.svg'
import logoDark from '../../../assets/branding/logo_horizontal_TextoBranco.svg'
import { api, urls } from '../../../services/api';
import { useEffect, useState } from 'react';
import { IMenuItem } from '../../../types/menu';
import { ItemMenuNav } from './components/ItemMenuNav';
import { useApp } from '../../../context/App';
import { Menu, X, Home, Search, Moon, Sun, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';

interface Props {
	search?: boolean
	dataSearch?: unknown[]
	onChange?: (array: unknown[]) => void
	route?: string
}

export function HeaderMobile({ route }: Props) {
	const { configDomainURL, tenant, getTenantUrl } = useApp()
	const domain = configDomainURL
	const [menu, setMenu] = useState<IMenuItem[]>([])
	const [idMenu, setIdMenu] = useState<number>(0)
	const [isOpen, setIsOpen] = useState<boolean>(false)
	const navigate = useNavigate();
	const { isDark, toggleTheme } = useTheme();

	const logo = isDark ? logoDark : logoLight;
	const isDefaultTenant = tenant.id === 'municipio';

	useEffect(() => {
		(async () => {
			const response = await api.get(urls.menus)
			setMenu(response.data)
		})()
	}, [])

	const toggleMenu = () => setIsOpen(!isOpen);

	return (
		<>
			<div className="w-full bg-white dark:bg-surface-dark shadow-sm dark:shadow-none dark:border-b dark:border-border-default-dark sticky top-[env(safe-area-inset-top)] z-50 lg:hidden transition-colors duration-300">
				<div className="w-11/12 mx-auto flex justify-between items-center py-4">
					<div className="flex items-center gap-3">
						<a href={getTenantUrl(domain)} className="transition-transform active:scale-95 shrink-0">
							<img
								src={logo}
								className="h-10 w-auto"
								alt="Logo"
							/>
						</a>

						{!isDefaultTenant && (
							<div className="flex items-center h-8 border-l-2 border-primary/20 dark:border-white/20 pl-3">
								<h1 className="font-roboto font-black text-lg text-primary dark:text-white uppercase tracking-tighter leading-none italic">
									{tenant.name.replace('Portal da ', '').replace('Portal do ', '')}
								</h1>
							</div>
						)}
					</div>

					<button
						onClick={toggleMenu}
						className="p-3 text-primary dark:text-on-surface-dark bg-surface-alt dark:bg-surface-dark-alt rounded-xl border-none active:bg-border-default dark:active:bg-border-default-dark transition-all duration-200 cursor-pointer shadow-sm hover:scale-105"
						aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'}
						aria-expanded={isOpen}
					>
						{isOpen ? <X size={24} /> : <Menu size={24} />}
					</button>
				</div>

				{route && (
					<div className="w-full bg-surface-alt/50 dark:bg-surface-dark-alt/50 border-y border-border-default dark:border-border-default-dark py-2 px-4 transition-colors">
						<div className="w-11/12 mx-auto font-bold text-[0.625rem] text-on-surface-subtle dark:text-on-surface-dark-muted uppercase tracking-wider">
							<a href={getTenantUrl("/")} className="text-inherit hover:text-primary dark:hover:text-secondary no-underline">Inicial</a>
							<span className="mx-2 text-border-default dark:text-border-default-dark">/</span>
							<span className="text-primary dark:text-on-surface-dark">{route}</span>
						</div>
					</div>
				)}
			</div>

			{/* Overlay */}
			<div
				className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] transition-opacity duration-500 lg:hidden ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
				onClick={toggleMenu}
				aria-hidden="true"
			/>

			{/* Sidebar Drawer */}
			<div
				className={`fixed top-0 right-0 h-full w-[85%] max-w-[20rem] bg-white dark:bg-surface-dark z-[70] shadow-2xl dark:shadow-premium-dark transition-transform duration-500 ease-in-out lg:hidden overflow-y-auto ${isOpen ? "translate-x-0" : "translate-x-full"}`}
				role="dialog"
				aria-label="Menu de navegação"
			>
				<div className="p-6 flex flex-col h-full min-h-screen">
					{/* Drawer Header */}
					<div className="flex justify-between items-center mb-6 pb-4 border-b border-border-default dark:border-border-default-dark">
						<img src={logo} className="h-8 w-auto" alt="Logo" />
						<button
							onClick={toggleMenu}
							className="p-2.5 text-on-surface-subtle dark:text-on-surface-dark-muted hover:text-primary dark:hover:text-secondary bg-surface-alt dark:bg-surface-dark-alt rounded-lg border-none cursor-pointer transition-all hover:rotate-90"
							aria-label="Fechar menu"
						>
							<X size={20} />
						</button>
					</div>

					{/* Quick Actions / Substitutes for BottomNav */}
					<div className="flex items-center justify-between mb-8 pb-6 border-b border-border-default/50 dark:border-border-default-dark/50 px-2 lg:hidden">
						<button
							onClick={() => { navigate(getTenantUrl('/')); toggleMenu(); }}
							className="flex flex-col items-center justify-center gap-1.5 text-on-surface-subtle dark:text-on-surface-dark-muted active:text-primary dark:active:text-secondary hover:text-primary dark:hover:text-secondary border-none bg-transparent cursor-pointer transition-colors"
							aria-label="Início"
						>
							<Home size={22} strokeWidth={2} />
							<span className="text-[9px] font-bold uppercase tracking-widest">Início</span>
						</button>

						<button
							onClick={() => { navigate(getTenantUrl('/busca')); toggleMenu(); }}
							className="flex flex-col items-center justify-center gap-1.5 text-on-surface-subtle dark:text-on-surface-dark-muted active:text-primary dark:active:text-secondary hover:text-primary dark:hover:text-secondary border-none bg-transparent cursor-pointer transition-colors"
							aria-label="Buscar"
						>
							<Search size={22} strokeWidth={2} />
							<span className="text-[9px] font-bold uppercase tracking-widest">Busca</span>
						</button>

						<button
							onClick={(e) => { e.preventDefault(); toggleTheme(); }}
							className="flex flex-col items-center justify-center gap-1.5 text-on-surface-subtle dark:text-on-surface-dark-muted active:text-primary dark:active:text-secondary hover:text-primary dark:hover:text-secondary border-none bg-transparent cursor-pointer transition-colors"
							aria-label={isDark ? 'Modo Claro' : 'Modo Escuro'}
						>
							{isDark ? <Sun size={22} strokeWidth={2} /> : <Moon size={22} strokeWidth={2} />}
							<span className="text-[9px] font-bold uppercase tracking-widest">{isDark ? 'Claro' : 'Escuro'}</span>
						</button>

						<a
							href="tel:4733416000"
							className="flex flex-col items-center justify-center gap-1.5 text-on-surface-subtle dark:text-on-surface-dark-muted active:text-primary dark:active:text-secondary hover:text-primary dark:hover:text-secondary no-underline border-none bg-transparent cursor-pointer transition-colors"
							aria-label="Ligar para Prefeitura"
						>
							<Phone size={22} strokeWidth={2} />
							<span className="text-[9px] font-bold uppercase tracking-widest">Contato</span>
						</a>
					</div>

					<nav className="flex flex-col flex-1" aria-label="Menu principal">

						<div className="flex flex-col gap-1">
							{menu.map((itemMenu, index) => (
								<ItemMenuNav
									key={index}
									idActual={idMenu}
									itemMenu={itemMenu}
									onChange={setIdMenu}
									onClose={() => setIsOpen(false)}
								/>
							))}
						</div>

						{/* Links Institucionais no Drawer */}
						<div className="mt-8 pt-8 border-t border-border-default dark:border-border-default-dark flex flex-col gap-4">
							<span className="text-[0.625rem] font-bold text-on-surface-subtle dark:text-on-surface-dark-muted uppercase tracking-[0.2em] mb-2 px-2">Links Institucionais</span>
							{[
								{ label: 'Ouvidoria', url: 'https://servicos.itajai.sc.gov.br/servico/79-ouvidoria.html' },
								{ label: 'e-SIC (Acesso à Informação)', url: 'https://servicos.itajai.sc.gov.br/servico/69-acesso_a_informacao.html' },
								{ label: 'Diário Oficial', url: 'https://intranet2.itajai.sc.gov.br/jornal-municipio/portal/' },
								{ label: 'Transparência', url: 'https://portaltransparencia.itajai.sc.gov.br/epublica-portal/#/itajai/portal?entidade=411' }
							].map((link, idx) => (
								<a
									key={idx}
									href={link.url}
									target="_blank"
									rel="noreferrer"
									className="px-4 py-3 text-sm font-medium text-on-surface-subtle dark:text-on-surface-dark-muted hover:text-primary dark:hover:text-secondary no-underline rounded-xl hover:bg-surface-alt dark:hover:bg-white/5 transition-colors"
								>
									{link.label}
								</a>
							))}
						</div>
					</nav>

					{/* Drawer Footer Status */}
					<div className="mt-12 py-6 text-center">
						<p className="text-[0.625rem] uppercase tracking-widest text-on-surface-subtle/50 dark:text-on-surface-dark-muted/30">
							Portal Municipal de Itajaí
						</p>
					</div>
				</div>
			</div>
		</>
	)
}